/**
 * Труба на плане: ломаная по полу от точки к точке — компонент раздела «Труба».
 *
 * Рисуется поверх стен и мебели линией в цвет среды: холодная синяя, горячая красная,
 * чуть толще кабеля. Концов точками не отмечаем: на них и так стоят символы точек
 * водоснабжения, лишние кружки только засоряют санузел.
 * Черновик трубы (PipeDraft) — та же линия пунктиром плюс отрезок к курсору.
 * Толщина линий — в пикселях экрана, чтобы труба не жирнела на зуме.
 */
import type { PointerEvent } from "react";
import type { Pt } from "@/shared/lib/geometry";
import { waterMediumColor, type WaterMedium } from "@/shared/catalog";

const SELECTED_COLOR = "#2f6feb";

const toPoints = (route: Pt[]) => route.map((p) => `${p.x},${p.y}`).join(" ");

interface Props {
	/** Полная трасса: от точки через повороты к точке */
	route: Pt[];
	medium: WaterMedium;
	selected: boolean;
	mmPerPx: number;
	interactive?: boolean;
	pipeId: string;
	onPointerDown?: (e: PointerEvent<SVGGElement>) => void;
}

export const PipeShape = ({
	route,
	medium,
	selected,
	mmPerPx,
	interactive = true,
	pipeId,
	onPointerDown,
}: Props) => {
	const px = (n: number) => n * mmPerPx;
	const own = waterMediumColor(medium);
	const color = selected ? SELECTED_COLOR : own;
	return (
		<g
			onPointerDown={onPointerDown}
			style={{
				cursor: interactive ? "pointer" : "default",
				pointerEvents: interactive ? "auto" : "none",
			}}
		>
			{selected && (
				<polyline
					points={toPoints(route)}
					fill="none"
					stroke="#2f6feb33"
					strokeWidth={px(8)}
					strokeLinejoin="round"
					strokeLinecap="round"
				/>
			)}
			<polyline
				points={toPoints(route)}
				fill="none"
				stroke={color}
				strokeWidth={px(2)}
				strokeLinejoin="round"
				strokeLinecap="round"
			/>
			{/* Широкая прозрачная линия — зона захвата, по тонкой не попасть */}
			<polyline
				data-role="pipe"
				data-id={pipeId}
				points={toPoints(route)}
				fill="none"
				stroke="transparent"
				strokeWidth={px(10)}
				strokeLinejoin="round"
				strokeLinecap="round"
			/>
		</g>
	);
};

interface DraftProps {
	/** От точки через уже поставленные повороты */
	route: Pt[];
	/** Куда ляжет следующий участок; null — курсор вне холста */
	preview: Pt | null;
	/** Курсор над точкой, принимающей среду: трубу можно подключить */
	canFinish: boolean;
	medium: WaterMedium;
	mmPerPx: number;
}

export const PipeDraft = ({ route, preview, canFinish, medium, mmPerPx }: DraftProps) => {
	const px = (n: number) => n * mmPerPx;
	const own = waterMediumColor(medium);
	const full = preview ? [...route, preview] : route;
	return (
		<g style={{ pointerEvents: "none" }}>
			<polyline
				points={toPoints(full)}
				fill="none"
				stroke={canFinish ? SELECTED_COLOR : own}
				strokeWidth={px(2.4)}
				strokeLinejoin="round"
				strokeLinecap="round"
				strokeDasharray={`${px(6)} ${px(3)}`}
			/>
			{route.map((p, i) => (
				<circle
					key={i}
					cx={p.x}
					cy={p.y}
					r={px(2.6)}
					fill="#ffffff"
					stroke={own}
					strokeWidth={px(1.4)}
				/>
			))}
			{preview && (
				<circle
					cx={preview.x}
					cy={preview.y}
					r={px(canFinish ? 5 : 3)}
					fill={canFinish ? "#2f6feb22" : "#ffffff"}
					stroke={canFinish ? SELECTED_COLOR : own}
					strokeWidth={px(1.4)}
				/>
			)}
		</g>
	);
};
